// turnstile.js
'use strict';
import loadScriptTag from '../resource/loadScriptTag.js';

const SCRIPT_ID = 'turnstile-api-script-id';
const TURNSTILE_ONLOAD = 'turnstileOnLoad';
const TURNSTILE_TIMEOUT = 5000;
const TURNSTILE_CHECK_INTERVAL = 500;
const TURNSTILE_MAX_TRY = 10;
// refer to: https://developers.cloudflare.com/turnstile/frequently-asked-questions/#how-long-is-a-turnstile-token-valid-for-before-it-is-rejected-by-siteverify
const TURNSTILE_TOKEN_VALID_TIME = 30 * 1000;
export const TURNSTILE_CONTAINER_ID = 'turnstile-invisible';

let mountResolve;
let mountReject;
const mountPromise = new Promise((resolve, reject) => {
  mountResolve = resolve;
  mountReject = reject;
});
let isResettingPromise;
let invisibleWidgetId;
let tokenResettingTime = -1;

/**
 * mount turnstile script
 * @param {object} [params={}]
 * @returns {Promise}
 */
export const mountTurnstileScript = async () => {
  if (document.getElementById(SCRIPT_ID)) return mountPromise;

  window[TURNSTILE_ONLOAD] = mountResolve;

  await loadScriptTag({
    id: SCRIPT_ID,
    onError: mountReject,
    async: true,
    src: `https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=${TURNSTILE_ONLOAD}`,
  });

  return mountPromise;
};

/**
 * render turnstile
 * @param {object} params
 * @param {string} params.container - container for turnstile
 * @param {string} params.sikekey - sitekey for turnstile
 * @param {'normal' | 'compact' | 'invisible'} params.size - the mode of turnstile
 * @param {any} params.otherParams
 * @returns {Promise<string>}
 */
export const renderTurnstile = async ({
  container,
  sitekey,
  size,
  ...otherParams
}) => {
  // invisible mode widget is rendered
  if (size === 'invisible' && invisibleWidgetId) return;

  try {
    await checkIsTurnstileReady();
    await checkContainerExist(container);

    const widgetId = await _renderTurnstile({
      container,
      sitekey,
      size,
      ...otherParams,
    });

    return widgetId;
  } catch (e) {
    throw new Error(e, 'render invisible turnstile error');
  }
};

// extract from renderTurnstile for testing
export const _renderTurnstile = async ({
  container,
  sitekey,
  size,
  ...otherParams
}) => {
  const widgetId = window.turnstile.render(container, {
    sitekey,
    size,
    ...otherParams,
  });

  if (size === 'invisible') invisibleWidgetId = widgetId;

  return widgetId;
};

/**
 * execute invisible mode turnstile
 * @returns {Promise<string>}
 */
export const executeTurnstile = async () => {
  try {
    await checkIsInvisibleTurnstileRendered();

    if (isResettingPromise) await isResettingPromise;

    // a turnstile token is valid for 300 seconds.
    // if the pre-reset token is expired, reset it again
    if (
      tokenResettingTime !== -1 &&
      Date.now() - tokenResettingTime >= TURNSTILE_TOKEN_VALID_TIME
    ) {
      await resetTurnstile();
    }

    const token = await _executeTurnstile();

    // pre-reset to reduce the time to get the next token
    resetTurnstile();

    return token;
  } catch (e) {
    throw new Error(e);
  }
};

// extract from executeTurnstile for testing
export const _executeTurnstile = () => {
  let checkTimer;
  let currentCheckTime = 0;
  return new Promise((resolve, reject) => {
    let token;

    token = window.turnstile.getResponse(invisibleWidgetId);

    if (token) resolve(token);
    else {
      checkTimer = setInterval(() => {
        currentCheckTime += 1;
        token = window.turnstile.getResponse(invisibleWidgetId);

        if (token) {
          clearInterval(checkTimer);
          resolve(token);
        } else if (currentCheckTime > TURNSTILE_MAX_TRY) {
          clearInterval(checkTimer);
          reject('get invisible token error');
        }
      }, TURNSTILE_CHECK_INTERVAL);
    }
  });
};

/**
 * reset turnstile for invisible mode
 */
export const resetTurnstile = async () => {
  if (!invisibleWidgetId) return;

  try {
    window.turnstile?.reset(invisibleWidgetId);

    let checkTimer;
    let currentCheckTime = 0;
    const turnsTileId = `${invisibleWidgetId}_response`;

    // reset is a sync function, so we need the promise to check whether the new token is generated or not
    // the promise can be used in executeTurnstile to wait for the new token to be generated
    isResettingPromise = new Promise((resolve, reject) => {
      checkTimer = setInterval(() => {
        currentCheckTime += 1;
        const newToken = document.getElementById(turnsTileId).value;

        if (newToken) {
          // record the resetting time of token
          tokenResettingTime = Date.now();
          clearInterval(checkTimer);
          resolve();
        } else if (currentCheckTime > TURNSTILE_MAX_TRY) {
          clearInterval(checkTimer);
          reject('turnstile resetting failed');
        }
      }, TURNSTILE_CHECK_INTERVAL);
    });
    return isResettingPromise;
  } catch (e) {
    throw new Error(e, 'reset invisible turnstile error');
  }
};

/**
 * check the turnstile script is loaded or not
 */
export const checkIsTurnstileReady = () => {
  let checkTimer;
  return new Promise((resolve, reject) => {
    checkTimer = setTimeout(() => {
      reject('mount script error, timeout');
    }, TURNSTILE_TIMEOUT);

    mountPromise
      .then(resolve)
      .catch(reject)
      .finally(() => {
        clearTimeout(checkTimer);
      });
  });
};

/**
 * when the turnstile needs to be executed immediately (such like OTP) and the TurnstileLoader component is not yet executed.
 * we need to check the mount tag for the turnstile exists or not
 */
export const checkContainerExist = container => {
  let checkTimer;
  let currentCheckTime = 0;
  return new Promise((resolve, reject) => {
    if (document.querySelector(container)) resolve();
    else {
      checkTimer = setInterval(() => {
        currentCheckTime += 1;
        if (document.querySelector(container)) {
          clearInterval(checkTimer);
          resolve();
        } else if (currentCheckTime > TURNSTILE_MAX_TRY) {
          clearInterval(checkTimer);
          reject('container does not exist');
        }
      }, TURNSTILE_CHECK_INTERVAL);
    }
  });
};

/**
 * check the invisible mode of turnstile is rendered or not
 */
export const checkIsInvisibleTurnstileRendered = (
  timeout = TURNSTILE_CHECK_INTERVAL
) => {
  let checkTimer;
  let currentCheckTime = 0;
  return new Promise((resolve, reject) => {
    if (invisibleWidgetId) resolve();
    else {
      checkTimer = setInterval(() => {
        currentCheckTime += 1;
        if (invisibleWidgetId) {
          clearInterval(checkTimer);
          resolve();
        } else if (currentCheckTime > TURNSTILE_MAX_TRY) {
          clearInterval(checkTimer);
          reject('render invisible turnstile error, timeout');
        }
      }, timeout);
    }
  });
};
